import axios from "axios";

export const PRODUCT_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || ""}${process.env.REACT_APP_ECOMMERCE_PERFIX || ""}`;

export type YourItemsProduct = {
    id: number;
    productIdStr?: string;
    badge?: string;
    title: string;
    description?: string;
    brand?: string;
    category?: string;
    superCategory?: string;
    image: string;
    colors?: {
        name: string;
        value: string;
    }[];
    extraColors?: number;
    rating?: number;
    reviews?: string | number;
    deal?: string;
    dealType?: string;
    price?: number;
    mrp?: number;
    delivery?: string;
    deliveryType?: "delivery" | "fast" | "stock";
    sellerId?: string;
    sellerSkuId?: string;
};

export const toNumericId = (s: string): number =>
    Math.abs(s.split("").reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) | 0, 0)) || 1;

/**
 * Fetch catalog products to enrich items with live price, mrp, colors, and stock details,
 * matching the pattern in WishlistPage.tsx
 */
export const fetchCatalogMap = async (): Promise<Map<string, any>> => {
    try {
        const res = await axios.get(`${PRODUCT_BASE_URL}/e-commerce/catalog/products`, {
            params: { limit: 48 },
        });
        const prods = res.data?.data?.products || res.data?.products || [];
        const map = new Map<string, any>();
        if (prods.length > 0) {
            prods.forEach((p: any) => {
                if (p._id) map.set(String(p._id), p);
                if (p.ssin) map.set(String(p.ssin), p);
            });
        }
        return map;
    } catch {
        return new Map();
    }
};

/**
 * Maps a wishlist item to YourItemsProduct using catalog enrichment,
 * identical to WishlistPage.tsx product details mapping.
 */
export const mapWishlistToYourItemsProduct = (
    item: any,
    catalogMap: Map<string, any>,
    idx: number = 0
): YourItemsProduct => {
    const itemIdStr = String(item._id || item.wishlistItemId || `item-${idx}`);
    const itemSsinStr = item.ssin ? String(item.ssin) : "";
    const catalog = catalogMap.get(itemIdStr) || (itemSsinStr ? catalogMap.get(itemSsinStr) : null);

    const title = catalog?.name || item.name || "Product";
    const image =
        item.mainImage ||
        catalog?.mainImage ||
        catalog?.images?.[0] ||
        "/assets/images/products/placeholder.png";

    const brandName =
        (typeof item.brand === "object" ? item.brand?.name : item.brand) ||
        (typeof catalog?.brand === "object" ? catalog.brand?.name : catalog?.brand) ||
        "";

    const categoryName =
        item.categoryHierarchy?.category ||
        catalog?.categoryHierarchy?.category ||
        item.categoryHierarchy?.superCategory ||
        catalog?.categoryHierarchy?.superCategory ||
        "General";

    const superCategoryName =
        item.categoryHierarchy?.superCategory ||
        catalog?.categoryHierarchy?.superCategory ||
        "All";

    const price = Number(
        catalog?.featuredOffer?.finalPrice ??
        catalog?.pricing?.sellingPrice ??
        catalog?.price ??
        item.price ??
        item.unitPrice ??
        0
    );

    const mrp = Number(
        catalog?.featuredOffer?.mrp ??
        catalog?.pricing?.mrp ??
        catalog?.mrp ??
        item.mrp ??
        (price > 0 ? price : 0)
    );

    const discountPercent =
        catalog?.featuredOffer?.discountPercent ??
        (mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0);

    const colors =
        catalog?.colors && catalog.colors.length > 0
            ? catalog.colors.map((c: any) => ({
                  name: c.name || c.colorName || "Standard",
                  value: c.hexCode || c.code || c.value || "#111827",
              }))
            : item.color
            ? [{ name: String(item.color), value: "#111827" }]
            : [];

    const rating = Number(
        item.reviews?.averageRating ??
        catalog?.reviews?.averageRating ??
        catalog?.rating ??
        0
    );

    const reviews = Number(
        item.reviews?.totalReviews ??
        catalog?.reviews?.totalReviews ??
        catalog?.reviewCount ??
        0
    );

    const isFast = Boolean(catalog?.featuredOffer?.fastDelivery);

    return {
        id: toNumericId(itemIdStr),
        productIdStr: itemIdStr,
        badge: discountPercent > 0 ? `${discountPercent}% OFF` : (catalog ? "Wishlisted" : undefined),
        title,
        description:
            catalog?.description ||
            item.note ||
            (categoryName ? `Explore the best of ${categoryName}` : ""),
        brand: brandName,
        category: categoryName,
        superCategory: superCategoryName,
        image,
        colors,
        extraColors: Math.max(0, colors.length - 3),
        rating: rating > 0 ? rating : undefined,
        reviews: reviews > 0 ? `${reviews}` : undefined,
        deal: catalog?.featuredOffer?.dealTitle || (discountPercent > 10 ? "Limited Time Deal" : undefined),
        dealType: catalog?.featuredOffer?.dealType || "deal-limited",
        price: price > 0 ? price : undefined,
        mrp: mrp > 0 ? mrp : undefined,
        delivery: isFast ? "Next-day Delivery" : (price > 0 ? "FREE Delivery" : undefined),
        deliveryType: isFast ? "fast" : "delivery",
        sellerId: catalog?.featuredOffer?.sellerId,
        sellerSkuId: catalog?.featuredOffer?.sellerSkuId,
    };
};

/**
 * Maps an order item to YourItemsProduct using catalog enrichment and order details
 */
export const mapOrderToYourItemsProduct = (
    prod: any,
    order: any,
    catalogMap: Map<string, any>
): YourItemsProduct => {
    const prodIdStr = String(prod.productId || prod.id || "");
    const prodSsinStr = prod.ssin ? String(prod.ssin) : "";
    const catalog = catalogMap.get(prodIdStr) || (prodSsinStr ? catalogMap.get(prodSsinStr) : null);

    const title = catalog?.name || prod.productName || "Product";
    const image =
        prod.productImage ||
        catalog?.mainImage ||
        catalog?.images?.[0] ||
        "/assets/images/products/placeholder.png";

    const brandName =
        (typeof catalog?.brand === "object" ? catalog.brand?.name : catalog?.brand) ||
        (typeof prod.brand === "object" ? prod.brand?.name : prod.brand) ||
        "";

    const categoryName =
        catalog?.categoryHierarchy?.category ||
        catalog?.categoryHierarchy?.superCategory ||
        (prod.productType && prod.productType !== "PHYSICAL" && prod.productType !== "ECOMMERCE" ? prod.productType : "") ||
        "General";

    const superCategoryName =
        catalog?.categoryHierarchy?.superCategory ||
        "All";

    const fallbackOrderPrice = order.payableAmount
        ? Math.round(order.payableAmount / Math.max(1, order.productDetails?.length || 1))
        : 0;

    const price = Number(
        catalog?.featuredOffer?.finalPrice ??
        catalog?.pricing?.sellingPrice ??
        catalog?.price ??
        prod.unitPrice ??
        prod.price ??
        fallbackOrderPrice
    );

    const fallbackOrderMrp = order.finalAmount?.amount
        ? Math.round(order.finalAmount.amount / Math.max(1, order.productDetails?.length || 1))
        : (price > 0 ? price : 0);

    const mrp = Number(
        catalog?.featuredOffer?.mrp ??
        catalog?.pricing?.mrp ??
        catalog?.mrp ??
        prod.mrp ??
        fallbackOrderMrp
    );

    const discountPercent =
        catalog?.featuredOffer?.discountPercent ??
        (mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0);

    const colors =
        catalog?.colors && catalog.colors.length > 0
            ? catalog.colors.map((c: any) => ({
                  name: c.name || c.colorName || "Standard",
                  value: c.hexCode || c.code || c.value || "#111827",
              }))
            : prod.color
            ? [{ name: String(prod.color), value: "#111827" }]
            : [];

    const rating = Number(
        catalog?.reviews?.averageRating ??
        catalog?.rating ??
        prod.rating ??
        0
    );

    const reviews = Number(
        catalog?.reviews?.totalReviews ??
        catalog?.reviewCount ??
        prod.reviews ??
        0
    );

    const isFast = Boolean(catalog?.featuredOffer?.fastDelivery);

    return {
        id: toNumericId(prodIdStr),
        productIdStr: prodIdStr,
        badge: discountPercent > 0 ? `${discountPercent}% OFF` : (catalog ? "Popular" : undefined),
        title,
        description:
            catalog?.description ||
            prod.description ||
            (categoryName ? `Explore the best of ${categoryName}` : ""),
        brand: brandName,
        category: categoryName,
        superCategory: superCategoryName,
        image,
        colors,
        extraColors: Math.max(0, colors.length - 3),
        rating: rating > 0 ? rating : undefined,
        reviews: reviews > 0 ? `${reviews}` : undefined,
        deal: catalog?.featuredOffer?.dealTitle || (discountPercent > 10 ? "Limited Time Deal" : undefined),
        dealType: catalog?.featuredOffer?.dealType || "deal-limited",
        price: price > 0 ? price : undefined,
        mrp: mrp > 0 ? mrp : undefined,
        delivery: isFast ? "Next-day Delivery" : (price > 0 ? "FREE Delivery" : undefined),
        deliveryType: isFast ? "fast" : "delivery",
        sellerId: catalog?.featuredOffer?.sellerId,
        sellerSkuId: catalog?.featuredOffer?.sellerSkuId,
    };
};

/**
 * Maps a saved CartItem to YourItemsProduct using catalog enrichment
 */
export const mapSavedCartItemToProduct = (
    item: any,
    catalogMap: Map<string, any>
): YourItemsProduct => {
    const prodIdStr = String(item.productId || item.id || "");
    const catalog = catalogMap.get(prodIdStr);

    const title = catalog?.name || item.productName || "Product";
    const image =
        item.productImage ||
        catalog?.mainImage ||
        catalog?.images?.[0] ||
        "/assets/images/products/placeholder.png";

    const price = Number(item.unitPrice ?? catalog?.featuredOffer?.finalPrice ?? catalog?.pricing?.sellingPrice ?? catalog?.price ?? 0);
    const mrp = Number(item.metadata?.mrp ?? catalog?.featuredOffer?.mrp ?? catalog?.pricing?.mrp ?? catalog?.mrp ?? (price > 0 ? price : 0));

    const discountPercent =
        catalog?.featuredOffer?.discountPercent ??
        (mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0);

    const colors =
        catalog?.colors && catalog.colors.length > 0
            ? catalog.colors.map((c: any) => ({
                  name: c.name || c.colorName || "Standard",
                  value: c.hexCode || c.code || c.value || "#111827",
              }))
            : item.metadata?.color
            ? [{ name: String(item.metadata.color), value: "#111827" }]
            : [];

    const rating = Number(
        catalog?.reviews?.averageRating ??
        catalog?.rating ??
        item.metadata?.rating ??
        0
    );

    const reviews = Number(
        catalog?.reviews?.totalReviews ??
        catalog?.reviewCount ??
        item.metadata?.reviews ??
        0
    );

    return {
        id: toNumericId(prodIdStr),
        productIdStr: prodIdStr,
        badge: "SAVED ITEM",
        title,
        description: String(item.metadata?.description || catalog?.description || ""),
        brand: catalog?.brand?.name || catalog?.brand || "",
        category: item.variantName || item.metadata?.category || catalog?.categoryHierarchy?.category || "",
        image,
        colors,
        extraColors: Math.max(0, colors.length - 3),
        rating: rating > 0 ? rating : undefined,
        reviews: reviews > 0 ? `${reviews}` : undefined,
        deal: discountPercent > 0 ? `${discountPercent}% OFF` : undefined,
        dealType: "deal-limited",
        price: price > 0 ? price : undefined,
        mrp: mrp > 0 ? mrp : undefined,
        delivery: catalog?.featuredOffer?.fastDelivery ? "Next-day Delivery" : (price > 0 ? "FREE Delivery" : undefined),
        deliveryType: catalog?.featuredOffer?.fastDelivery ? "fast" : "delivery",
    };
};
